import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function Welcome() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [applied, setApplied] = useState(false);
  const [applying, setApplying] = useState(false);

  const handleApplyCode = async () => {
    if (!code.trim()) return;
    setApplying(true);
    setError('');
    const result = await db.validateAndApplyReferralCode(user.id, code.trim());
    setApplying(false);
    if (result.success) {
      setApplied(true);
      refreshProfile();
    } else {
      setError(result.error || 'Could not apply code. Please try again.');
    }
  };

  return (
    <div className="h-[100dvh] bg-primary flex flex-col items-center justify-center px-6 text-center animate-fade-in" data-testid="welcome-screen">
      <h1 className="text-3xl sm:text-4xl font-bold text-primary-foreground tracking-tight" data-testid="welcome-heading">
        Welcome to Jiv, {profile?.display_name}.
      </h1>
      <p className="text-primary-foreground/75 mt-3 max-w-xs leading-relaxed text-sm">
        Your profile is created. Add a referral code now if you have one, or skip and wait for selfie review.
      </p>

      {profile?.vouch_status !== 'verified' && (
        <div className="mt-8 w-full max-w-xs">
          {!applied ? (
            <div className="space-y-3">
              <p className="text-primary-foreground/60 text-sm">Have a referral code? Enter it to join as Jiv Verified.</p>
              <div className="flex gap-2">
                <Input
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleApplyCode()}
                  placeholder="e.g. JIV-K7X2QP"
                  className="bg-white/10 border-white/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-xl"
                  data-testid="referral-code-input"
                  disabled={applying}
                />
                <Button
                  onClick={handleApplyCode}
                  variant="secondary"
                  className="shrink-0 rounded-xl"
                  disabled={applying || !code.trim()}
                  data-testid="apply-referral-btn"
                >
                  {applying ? '...' : 'Apply'}
                </Button>
              </div>
              {error && <p className="text-primary-foreground/80 text-sm" data-testid="referral-error">{error}</p>}
              <button onClick={() => navigate('/')} className="text-primary-foreground/60 text-sm block w-full" data-testid="skip-referral-btn">
                I don't have a code
              </button>
            </div>
          ) : (
            <p className="text-primary-foreground font-semibold text-sm" data-testid="referral-success">
              You're now Verified. Welcome to Jiv.
            </p>
          )}
        </div>
      )}

      <Button
        onClick={() => navigate('/')}
        className="mt-8 bg-white text-primary hover:bg-white/90 h-12 px-8 text-base rounded-xl font-semibold"
        data-testid="start-exploring-btn"
      >
        Go to review
      </Button>
    </div>
  );
}
